import { find, sortBy, isEqualWith, isEqual, isEmpty } from 'lodash';
import { observable, makeObservable, action } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { EngineTab } from '@/constants';
import { UnitMeasurementSystem } from '@/constants/enums';
import { EngineStepsEnum, EngineSubstepsEnum, SensorsLogicEnum, SensorsAvailabilityEnum, ValidationTriggerFromStepEnum } from './constants';
import { convertNullToInfinity, convertPressureToImperial, validateSetpoints } from './utils';
import { RulesBehaviour, Setpoint } from './common';

export class SensorsConfiguration {
  @observable flowSensorsPriority: SensorPriority;
  @observable pressureSensorsPriority: SensorPriority;

  constructor(sensors?, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.flowSensorsPriority = new SensorPriority(sensors?.flowSensorsPriority, tvalue, unitMeasurementSystem, 'flow');
    this.pressureSensorsPriority = new SensorPriority(
      sensors?.pressureSensorsPriority,
      tvalue,
      unitMeasurementSystem,
      'pressure',
    );
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    return {
      flowSensorsPriority: this.flowSensorsPriority.getValuesToSave(tvalue, unitMeasurementSystem),
      pressureSensorsPriority: this.pressureSensorsPriority.getValuesToSave(tvalue, unitMeasurementSystem),
    };
  }

  hasChanges() {
    return this.flowSensorsPriority?.hasChanges() || this.pressureSensorsPriority?.hasChanges();
  }

  cancelChanges() {
    this.flowSensorsPriority?.cancelChanges();
    this.pressureSensorsPriority?.cancelChanges();
  }
}

export class SensorPriority extends RulesBehaviour {
  constructor(sensorPriority, tvalue, unitMeasurementSystem, type?) {
    const sensorsConfiguration = window.localStorage.getItem(EngineStepsEnum.SensorsConfiguration);
    const oldSensorsConfiguration = JSON.parse(sensorsConfiguration, convertNullToInfinity)?.[
      type === 'flow' ? EngineSubstepsEnum.FlowSensorsPriority : EngineSubstepsEnum.PressureSensorsPriority
    ];
    super(sensorPriority, oldSensorsConfiguration, tvalue, unitMeasurementSystem);
    makeObservable(this);
    this.sensorsPriority = sensorPriority?.sensorsPriority?.map(priority => new Priority(priority)) ?? [
      new Priority({
        key: uuidv4(),
        priority: this.sensorsPriority?.[this.sensorsPriority?.length - 1]?.priority ?? 0,
        aggregation: type === 'flow' ? SensorsLogicEnum.Sum : SensorsLogicEnum.Average,
        availability: SensorsAvailabilityEnum.All,
      }),
    ];
    if (sensorsConfiguration) {
      this.oldValues = {
        ...oldSensorsConfiguration,
        rules: oldSensorsConfiguration?.rules?.map(
          rule =>
            new Setpoint({
              ...rule,
              ...(unitMeasurementSystem === UnitMeasurementSystem.Imperial && {
                load: convertPressureToImperial(rule?.load, tvalue),
                unload: convertPressureToImperial(rule?.unload, tvalue),
                autoStop: convertPressureToImperial(rule?.autoStop, tvalue),
                immediateStop: convertPressureToImperial(rule?.immediateStop, tvalue),
                vsdTarget: convertPressureToImperial(rule?.vsdTarget, tvalue),
              }),
            }),
        ),
        sensorsPriority: oldSensorsConfiguration?.sensorsPriority?.map(priority => new Priority(priority)),
      };
    }
  }

  @action deleteSensorPriority(sensorPriorityKey) {
    const deletedSensorPriority = find(this.sensorsPriority, { key: sensorPriorityKey })?.priority;
    this.sensorsPriority = this.sensorsPriority.reduce(
      (result, current) => [
        ...result,
        ...(current?.key !== sensorPriorityKey
          ? [
              new Priority({
                ...current,
                priority: current?.priority > deletedSensorPriority ? current?.priority - 1 : current?.priority,
              }),
            ]
          : []),
      ],
      [],
    );
  }

  @action moveSensorPriority(from, to) {
    this.sensorsPriority = sortBy(
      this.sensorsPriority.map(
        (sensorPriority, index) =>
          new Priority({
            ...sensorPriority,
            priority: sensorPriority?.priority + (to - from) * (index === from ? 1 : index === to ? -1 : 0),
          }),
      ),
      'priority',
    );
  }

  @action addSensorPriority(type) {
    this.sensorsPriority = [
      ...this.sensorsPriority,
      new Priority({
        key: uuidv4(),
        priority: this.sensorsPriority?.[this.sensorsPriority?.length - 1]?.priority + 1,
        aggregation: type === 'flow' ? SensorsLogicEnum.Sum : SensorsLogicEnum.Average,
        availability: SensorsAvailabilityEnum.All,
      }),
    ];
  }

  @action setSensorPriorityField(key, field, value) {
    this.sensorsPriority = this.sensorsPriority.map(sensorPriority =>
      sensorPriority?.key === key ? new Priority({ ...sensorPriority, [field]: value }) : sensorPriority,
    );
  }

  validate(_, args?, triggerFromStep?: ValidationTriggerFromStepEnum) {
    let validationMessages = [];
    if (triggerFromStep != ValidationTriggerFromStepEnum.EngineStep) {
      validationMessages = validateSetpoints(this.rules);
    }

    this.sensorsPriority?.forEach(sensorPriority => {
      const areSensorsEqual = this.sensorsPriority?.some(
        priority =>
          priority.aggregation === sensorPriority.aggregation &&
          priority.availability === sensorPriority.availability &&
          priority.key !== sensorPriority.key &&
          sensorPriority?.sensors?.length === priority?.sensors?.length &&
          isEqualWith(sortBy(sensorPriority.sensors, 'id'), sortBy(priority.sensors, 'id'), isEqual),
      );

      if (!sensorPriority?.sensors?.length)
        validationMessages.push({
          key: sensorPriority?.key,
          field: 'sensors',
          message: 'sensorsAreMandatory',
          focused: typeof args === 'string' ? args === `sensors-${sensorPriority?.key}` : true,
          tab: EngineTab.SensorsConfiguration,
        });

      if (areSensorsEqual)
        validationMessages.push({
          key: sensorPriority?.key,
          field: 'sensors',
          message: 'sensorFlowCombinationMustBeUnique',
          focused: typeof args === 'string' ? args === `sensors-${sensorPriority?.key}` : true,
          tab: EngineTab.SensorsConfiguration,
        });
    });
    return validationMessages;
  }

  hasChanges() {
    return (
      super.hasChanges() ||
      (!isEmpty(this.oldValues) &&
        (this.sensorsPriority?.length !== this.oldValues?.sensorsPriority?.length ||
          this.sensorsPriority?.some(sensorPriority => {
            const oldSensorPriority = find(this.oldValues?.sensorsPriority, { key: sensorPriority.key });
            return (
              sensorPriority?.priority !== oldSensorPriority?.priority ||
              sensorPriority?.aggregation !== oldSensorPriority?.aggregation ||
              sensorPriority?.availability !== oldSensorPriority?.availability ||
              !isEqual(sensorPriority?.sensors, oldSensorPriority?.sensors)
            );
          })))
    );
  }

  cancelChanges() {
    super.cancelChanges();
    this.sensorsPriority = this.oldValues?.sensorsPriority;
  }
}

export class Priority {
  @observable key: string;
  @observable priority: number;
  @observable aggregation: SensorsLogicEnum;
  @observable availability: SensorsAvailabilityEnum;
  @observable sensors: Sensor[];

  constructor(priority) {
    makeObservable(this);
    this.key = priority?.key;
    this.priority = priority?.priority;
    this.aggregation = priority?.aggregation;
    this.availability = priority?.availability;
    this.sensors = priority?.sensors?.map(sensor => new Sensor(sensor));
  }

  getPriorityName(t) {
    return this.priority === 0 ? t('main') : t('fallback', { priority: this.priority });
  }

  getSensorNames?() {
    const sensorNames = this.sensors?.map(sensor => sensor?.name);
    return sensorNames?.join(' + ');
  }
}

class Sensor {
  @observable id: string;
  @observable name: string;

  constructor(sensor) {
    makeObservable(this);
    this.id = sensor?.id;
    this.name = sensor?.name;
  }
}
